import React from 'react';

import DriveFileMove from 'icons/drive-file-move.svg';
import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import useRequestError from 'components/hooks/useRequestError';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ErrorMessage from 'components/application/ErrorMessage';
import ModalBox from 'components/application/ModalBox';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useProjectRelocationModalContext } from 'components/projectsDashboard/contexts/ProjectRelocationModalContext';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

export default function GroupIntoFolderModal({
  onClose,
  onMoveToExistingFolderClick,
  onSuccess,
  selectedProjectIds,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const {
    createFolderMutation,
    creationBlockingExistingFolder,
    currentFolder,
    handleCurrentFolderInputChange,
    handleDestinationFolderSelect,
    handleReset,
  } = useProjectRelocationModalContext();

  const [
    requestError,
    ,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.GroupIntoFolderModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleSubmit = () => {
    createFolderMutation.mutate(
      { folderParams: { ...currentFolder, project_ids: selectedProjectIds } },
      {
        onSuccess: (data) => {
          resetRequestError();
          handleReset();
          onSuccess({ folderId: data.data.id });
        },
        onError: (error) => {
          if (error.response.status === 422) {
            handleRequestError(error);
          } else {
            breadBoard.addInedibleToast({
              fullMessage: i18nTCommon('projects.errors.folderCreation'),
            });
          }
        },
      },
    );
  };

  const count = selectedProjectIds.length;

  const ramsName = pluralizeProjectsName({
    count,
    currentActor,
  });

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={() => {
              handleReset();
              onClose();
            }}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              currentFolder.name.length === 0 || createFolderMutation.isLoading
            }
            onClick={handleSubmit}
          >
            {i18nTCommon('projects.createFolder')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={() => {
        handleReset();
        onClose();
      }}
    >
      <>
        <div className='tw-mx-6 tw-mt-6'>
          <h2 className='modalbox-header__title truncated-text-container tw-text-xl tw-font-semibold tw-text-grey-900'>
            {i18nT('title', {
              count,
              interpolation: { escapeValue: false },
              ramsName,
            })}
          </h2>
        </div>
        <div className='modalbox-body'>
          {Object.keys(requestError.validationErrors).length === 0 && (
            <div className='tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
              <div className='tw-mr-3 tw-h-5'>
                <InfoIcon
                  className='[&_path]:tw-fill-cyan-800'
                  height={20}
                  width={20}
                />
              </div>
              <p className='tw-mb-0'>
                <Trans
                  i18nKey='projectsDashboard.components.GroupIntoFolderModal.description'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ ramsName }}
                />
              </p>
            </div>
          )}
          {creationBlockingExistingFolder ?
            <div className='tw-mb-6 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-4 tw-text-red-800'>
              <p className='tw-mb-2 tw-text-m tw-font-semibold'>
                {i18nT('existingFolder.title')}
              </p>
              <p className='tw-mb-2'>
                {i18nT('existingFolder.description', {
                  interpolation: { escapeValue: false },
                  ramsName,
                })}
              </p>
              <button
                className='tw-flex tw-h-8 tw-max-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-300 tw-bg-white tw-pl-2.5 tw-pr-4 tw-font-medium tw-text-grey-900'
                onClick={() => {
                  handleDestinationFolderSelect(
                    creationBlockingExistingFolder.id,
                  );

                  onMoveToExistingFolderClick();
                }}
                type='button'
              >
                <DriveFileMove
                  className='tw-flex-none [&_path]:tw-fill-grey-900'
                  height={20}
                  width={20}
                />
                <p className='tw-mb-0 tw-truncate'>
                  {i18nT('existingFolder.button', {
                    folderName: creationBlockingExistingFolder.attributes.name,
                    interpolation: { escapeValue: false },
                  })}
                </p>
              </button>
            </div>
          : <ErrorMessage
              isFallback={false}
              modifiers='tw-mb-3'
              validationErrors={requestError.validationErrors}
            />
          }
          <TextField
            autoFocus={true}
            fieldError={requestError.validationErrors.name}
            inputClassName='tw-px-3 tw-py-[9px]'
            isRequired={true}
            label={i18nTCommon('projects.folderName')}
            name='name'
            onChange={handleCurrentFolderInputChange}
            placeholder={i18nTCommon('projects.enterUniqueNameForFolder')}
            removeErrorStyling={removeErrorStyling}
            value={currentFolder.name}
          />
        </div>
      </>
    </ModalBox>
  );
}

GroupIntoFolderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onMoveToExistingFolderClick: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};
